<template>
  <v-container>
    <div>
      <div class="d-flex justify-end mb-4">
        <v-btn
          color="primary"
          :ripple="false"
          small
          @click="openCreateDialog"
        >
          + 指標追加
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="items"
        :footer-props="{ itemsPerPageText: '行/ページ:' }"
        no-data-text="データがありません"
      >
        <template #[`item.attributes.metricItemLabels`]="{ item }">
          <v-chip
            v-for="label in item.attributes.metricItemLabels"
            :key="label"
            x-small
            class="mr-1"
          >
            {{ label }}
          </v-chip>
        </template>

        <template #[`item.attributes.iconKey`]="{ item }">
          <v-icon
            small
            :color="item.attributes.iconColor || '#9E9E9E'"
          >
            {{ resolveIcon(item.attributes.iconKey) }}
          </v-icon>
        </template>

        <template #[`item.attributes.iconColor`]="{ item }">
          <span
            class="d-inline-block rounded"
            :style="{ background: item.attributes.iconColor || '#9E9E9E', width: '20px', height: '20px' }"
          />
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            :ripple="false"
            small
            @click="openItemsDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiFormatListBulleted }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            :ripple="false"
            @click="openItemsDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>
          <v-dialog max-width="400">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>
              </v-btn>
            </template>
            <template #default="dialog">
              <v-card>
                <v-card-title>削除の確認</v-card-title>
                <v-card-text>
                  この指標を削除しますか？この操作は取り消せません。
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    @click="dialog.value = false"
                  >
                    キャンセル
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    @click="destroyItem(item.id); dialog.value = false"
                  >
                    削除
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </div>

    <!-- 指標アイテム管理モーダル -->
    <report-metric-items-dialog
      v-model="itemsDialog"
      :metric-id="selectedMetricId"
      :metric-name="selectedMetricName"
    />

    <!-- 指標 追加・編集ダイアログ -->
    <v-dialog
      v-model="editDialog"
      max-width="500"
      @click:outside="resetParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title>{{ isNew ? '新規作成' : '編集' }}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="params.name"
                  label="名前"
                  autofocus
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="params.reportSectionId"
                  :items="sections"
                  label="セクション"
                  item-text="label"
                  item-value="id"
                />
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="params.iconKey"
                  :items="iconOptions"
                  item-value="key"
                  label="アイコン"
                  clearable
                >
                  <template #item="{ item }">
                    <v-icon
                      class="mr-2"
                      :color="params.iconColor || undefined"
                    >
                      {{ item.path }}
                    </v-icon>
                    {{ item.label }}
                  </template>
                  <template #selection="{ item }">
                    <v-icon
                      class="mr-2"
                      :color="params.iconColor || undefined"
                    >
                      {{ item.path }}
                    </v-icon>
                    {{ item.label }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="params.roundingMode"
                  :items="roundingModeOptions"
                  label="端数処理"
                />
              </v-col>
              <v-col cols="12">
                <div class="text-caption mb-1 grey--text">
                  アイコンカラー
                </div>
                <v-menu
                  v-model="colorPickerOpen"
                  :close-on-content-click="false"
                  offset-y
                >
                  <template #activator="{ on, attrs }">
                    <div
                      class="rounded cursor-pointer"
                      :style="{
                        background: params.iconColor || '#9E9E9E',
                        width: '36px',
                        height: '36px',
                        border: '2px solid rgba(255,255,255,0.2)',
                      }"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-card>
                    <v-color-picker
                      v-model="params.iconColor"
                      mode="hexa"
                      hide-mode-switch
                    />
                    <v-card-text
                      v-if="usedColors.length"
                      class="pt-0"
                    >
                      <div class="d-flex flex-wrap mt-2">
                        <div
                          v-for="color in usedColors"
                          :key="color"
                          class="rounded cursor-pointer ma-1"
                          :style="{
                            background: color,
                            width: '28px',
                            height: '28px',
                            border: params.iconColor === color
                              ? '2px solid rgba(255,255,255,0.9)'
                              : '1px solid rgba(255,255,255,0.2)',
                          }"
                          @click="params.iconColor = color"
                        />
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-btn
                  v-if="params.iconColor"
                  text
                  x-small
                  class="mt-1 px-0"
                  @click="params.iconColor = null"
                >
                  クリア
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              small
              @click="closeDialog(dialog)"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!params.name || !params.reportSectionId"
              @click="saveItem(dialog)"
            >
              {{ isNew ? '作成' : '更新' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
} from '@vue/composition-api'
import {
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiFormatListBulleted,
  mdiTrendingUp,
  mdiCurrencyJpy,
  mdiChartBar,
  mdiChartLine,
  mdiDragVertical,
} from '@mdi/js'
import ReportMetricApi from '@/api/v1/admin/ReportMetric'
import ReportMetricItemsDialog from '@/views/v2-temp/report-metric-items/ReportMetricItemsDialog.vue'
import sortableDataTable from '@/directives/sortableDataTable'

export default {
  components: { ReportMetricItemsDialog },
  directives: { sortableDataTable },
  setup() {
    const items = ref([])
    const sections = ref([])
    const loading = ref(false)
    const editDialog = ref(false)
    const colorPickerOpen = ref(false)
    const itemsDialog = ref(false)
    const selectedMetricId = ref(null)
    const selectedMetricName = ref('')
    const roundingModeOptions = [
      { text: '四捨五入', value: 'round' },
      { text: '切り捨て', value: 'floor' },
      { text: '切り上げ', value: 'ceil' },
      { text: 'なし', value: 'none' },
    ]

    const params = reactive({
      id: null,
      name: '',
      reportSectionId: null,
      iconKey: null,
      iconColor: null,
      roundingMode: 'round',
    })

    const isNew = computed(() => params.id === null)

    const iconOptions = [
      { key: 'mdiChartLine', path: mdiChartLine, label: '折れ線グラフ（デフォルト）' },
      { key: 'mdiTrendingUp', path: mdiTrendingUp, label: 'トレンド上昇' },
      { key: 'mdiCurrencyJpy', path: mdiCurrencyJpy, label: '円マーク' },
      { key: 'mdiChartBar', path: mdiChartBar, label: '棒グラフ' },
    ]

    // 既存の指標で使われているカラーをスウォッチとして表示
    const usedColors = computed(() => [...new Set(
      items.value.map(i => i.attributes.iconColor).filter(Boolean),
    )])

    const ICON_MAP = Object.fromEntries(iconOptions.map(o => [o.key, o.path]))
    const resolveIcon = key => ICON_MAP[key] || mdiChartLine

    const headers = [
      { text: '名前', value: 'attributes.name', width: '15%' },
      {
        text: 'セクション', value: 'attributes.sectionLabel', sortable: false, width: '15%',
      },
      { text: '計測項目', value: 'attributes.metricItemLabels', sortable: false },
      {
        text: 'アイコン', value: 'attributes.iconKey', sortable: false, width: '10%',
      },
      {
        text: 'アイコンカラー', value: 'attributes.iconColor', sortable: false, width: '12%',
      },
      {
        text: '', value: 'actions', sortable: false, align: 'end', width: '22%',
      },
    ]

    // セクションごとにグループ化
    const sectionGroups = computed(() => {
      const groups = sections.value.map(section => ({
        label: section.label,
        id: section.id,
        items: items.value.filter(i => i.attributes.sectionLabel === section.label),
      }))

      const unsectioned = items.value.filter(i => !i.attributes.sectionLabel)
      if (unsectioned.length) {
        groups.push({ label: 'セクションなし', id: null, items: unsectioned })
      }

      return groups
    })

    const fetchItems = async () => {
      loading.value = true
      try {
        const res = await ReportMetricApi.getList()
        const raw = res.data.reportMetrics?.data || []
        sections.value = res.data.meta?.sections || []
        const sectionOrder = sections.value.map(s => s.label)
        items.value = [...raw].sort((a, b) => {
          const aIdx = sectionOrder.indexOf(a.attributes.sectionLabel)
          const bIdx = sectionOrder.indexOf(b.attributes.sectionLabel)

          return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx)
        })
      } catch (e) {
        // TODO
      } finally {
        loading.value = false
      }
    }

    const openItemsDialog = item => {
      selectedMetricId.value = item.id
      selectedMetricName.value = item.attributes.name
      itemsDialog.value = true
    }

    watch(itemsDialog, val => {
      if (!val) fetchItems()
    })

    const resetParams = () => {
      params.id = null
      params.name = ''
      params.reportSectionId = null
      params.iconKey = null
      params.iconColor = null
      params.roundingMode = 'round'
    }

    const openCreateDialog = () => {
      resetParams()
      editDialog.value = true
    }

    const openEditDialog = item => {
      params.id = item.id
      params.name = item.attributes.name
      params.reportSectionId = item.attributes.reportSectionId || null
      params.iconKey = item.attributes.iconKey || null
      params.iconColor = item.attributes.iconColor || null
      editDialog.value = true
    }

    const closeDialog = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const saveItem = async dialog => {
      try {
        const payload = {
          name: params.name,
          report_section_id: params.reportSectionId,
          icon_key: params.iconKey,
          icon_color: params.iconColor,
          rounding_mode: params.roundingMode,
        }
        if (isNew.value) {
          await ReportMetricApi.create(payload)
        } else {
          await ReportMetricApi.update(params.id, payload)
        }
        closeDialog(dialog)
        fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const destroyItem = async id => {
      try {
        await ReportMetricApi.destroy(id)
        await fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const onSorted = async event => {
      const { oldIndex, newIndex } = event
      if (oldIndex === newIndex) return

      const movedItem = items.value[oldIndex]
      const sectionLabel = movedItem.attributes.sectionLabel || null

      const moved = items.value.splice(oldIndex, 1)[0]
      items.value.splice(newIndex, 0, moved)

      const sectionItems = items.value.filter(
        i => (i.attributes.sectionLabel || null) === sectionLabel,
      )
      const positions = sectionItems.map((item, idx) => ({ id: item.id, position: idx }))
      try {
        await ReportMetricApi.updatePositions({ positions })
      } catch (e) {
        // TODO
      }
    }

    onMounted(fetchItems)

    return {
      items,
      sections,
      iconOptions,
      resolveIcon,
      usedColors,
      loading,
      editDialog,
      colorPickerOpen,
      itemsDialog,
      selectedMetricId,
      selectedMetricName,
      params,
      isNew,
      headers,
      openItemsDialog,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      saveItem,
      destroyItem,
      resetParams,
      onSorted,
      roundingModeOptions,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiFormatListBulleted,
        mdiDragVertical,
      },
    }
  },
}
</script>
